// The text a person pastes into Claude to create each scheduled routine
// (GUIDED_SETUP_BUILD_SPEC §3.4). Times, days and the zone come from Cadence,
// so what Claude schedules is exactly what Cadence checks the runs against.

import { setupCopy } from '../../copy/setup';

const ALL_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const WEEKDAYS = ALL_DAYS.slice(0, 5);

function fill(template: string, values: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (m, k: string) => values[k] ?? m);
}

/** "08:30", "08:30 and 11:00", "08:30, 11:00 and 13:30". */
export function joinList(items: string[]): string {
  return items.length <= 1 ? (items[0] ?? '') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/** Exactly Monday to Friday, which Claude's Weekdays preset covers. */
export function isWeekdays(workingDays: string[]): boolean {
  const days = ALL_DAYS.filter((d) => workingDays.includes(d));
  return days.length === WEEKDAYS.length && WEEKDAYS.every((d) => days.includes(d));
}

/** "on weekdays" for Monday to Friday, otherwise "on Mondays, Tuesdays and Thursdays". */
export function daysPhrase(workingDays: string[]): string {
  const days = ALL_DAYS.filter((d) => workingDays.includes(d));
  if (isWeekdays(days)) return setupCopy.routinePrompt.weekdays;
  return fill(setupCopy.routinePrompt.onDays, { days: joinList(days.map((d) => `${d}s`)) });
}

export interface RoutinePrompt {
  routine: 'sweep' | 'close' | 'prepare';
  name: string;
  times: string[];
  prompt: string;
  /** The per-run instruction, for setting a task up by hand. */
  run: string;
}

export function routinePrompt(
  routine: 'sweep' | 'close' | 'prepare', times: string[], workingDays: string[], timeZone: string,
): RoutinePrompt {
  const c = setupCopy.routinePrompt;
  const name = routine === 'sweep' ? c.sweepName : routine === 'close' ? c.closeName : c.prepareName;
  const run = routine === 'sweep' ? c.sweepRun : routine === 'close' ? c.closeRun : c.prepareRun;
  return {
    routine,
    name,
    times,
    run,
    prompt: fill(routine === 'sweep' ? c.createSweep : c.createOnce,
      { name, days: daysPhrase(workingDays), times: joinList(times), zone: timeZone, run }),
  };
}
